import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BackupsCard } from '../../components/admin/BackupsCard';
import { EmailCenterCard } from '../../components/admin/EmailCenterCard';
import { Building2, Users, Plane, DollarSign, TrendingUp, AlertCircle, Package, Headphones, BarChart3 } from 'lucide-react';

export default function AdminDashboardPage() {
    const { apiCall, admin } = useAdminAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await apiCall('get', '/admin/dashboard');
                setData(res.data);
            } catch (e) {
                console.error('Dashboard error:', e);
                toast.error('No se pudo cargar el panel');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [apiCall]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const tenants = data?.tenants || {};
    const usage = data?.usage || {};
    const revenue = data?.revenue || {};
    const support = data?.support || {};
    const leads = data?.leads || {};
    const tenantUsage = data?.tenant_usage || [];

    const kpis = [
        { label: 'Total Empresas', value: tenants.total || 0, icon: Building2, color: 'text-amber-500', bg: 'bg-amber-500/10' },
        { label: 'Aerolíneas', value: tenants.airlines || 0, icon: Plane, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
        { label: 'Ground Handling', value: tenants.ground_handlers || 0, icon: Package, color: 'text-violet-500', bg: 'bg-violet-500/10' },
        { label: 'Activas / Prueba', value: `${tenants.active || 0} / ${tenants.trial || 0}`, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
        { label: 'Suspendidas', value: tenants.suspended || 0, icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
        { label: 'Usuarios Totales', value: usage.total_users || 0, icon: Users, color: 'text-sky-500', bg: 'bg-sky-500/10' },
        { label: 'Aeronaves', value: usage.total_aircraft || 0, icon: Plane, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
        { label: 'A cobrar / mes', value: `$${(revenue.to_collect ?? revenue.mrr ?? 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
        { label: 'En piloto (lista)', value: `$${(revenue.in_pilot || 0).toLocaleString()}`, icon: DollarSign, color: 'text-sky-500', bg: 'bg-sky-500/10' },
    ];

    const statusColors = {
        active: 'bg-emerald-500/10 text-emerald-500',
        trial: 'bg-sky-500/10 text-sky-500',
        suspended: 'bg-red-500/10 text-red-500',
        cancelled: 'bg-muted text-muted-foreground',
    };
    const statusLabels = { active: 'Activa', trial: 'Prueba', suspended: 'Suspendida', cancelled: 'Cancelada' };

    const planColors = {
        free: 'bg-zinc-500/10 text-zinc-400', basic: 'bg-sky-500/10 text-sky-400',
        pro: 'bg-amber-500/10 text-amber-400', enterprise: 'bg-violet-500/10 text-violet-400',
        gh_starter: 'bg-teal-500/10 text-teal-400', gh_pro: 'bg-teal-500/10 text-teal-400',
        gh_enterprise: 'bg-teal-500/10 text-teal-400',
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="admin-dashboard-title">
                        Panel de Control
                    </h1>
                    <p className="text-muted-foreground mt-1">Vista global de la plataforma AeroOps</p>
                </div>
                <div className="flex items-center gap-3">
                    <Badge
                        className={`text-xs px-3 py-1 uppercase tracking-wider ${data?.environment === 'production' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' : 'bg-amber-500/10 text-amber-500 border-amber-500/30'}`}
                        variant="outline"
                        data-testid="env-badge"
                    >
                        {data?.environment === 'production' ? 'Producción' : `Entorno: ${data?.environment || 'desconocido'}`}
                    </Badge>
                    {(support.open_tickets || 0) > 0 && (
                        <Badge variant="destructive" className="text-xs px-3 py-1" data-testid="open-tickets-badge">
                            <Headphones className="w-3 h-3 mr-1" />{support.open_tickets} tickets abiertos
                        </Badge>
                    )}
                    {(leads.new || 0) > 0 && (
                        <Badge className="bg-orange-500/10 text-orange-400 text-xs px-3 py-1">
                            <TrendingUp className="w-3 h-3 mr-1" />{leads.new} leads nuevos
                        </Badge>
                    )}
                </div>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-kpi-grid">
                {kpis.map((kpi) => {
                    const Icon = kpi.icon;
                    return (
                        <Card key={kpi.label} className="card-aviation">
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs font-bold tracking-widest text-muted-foreground uppercase">{kpi.label}</p>
                                        <p className="text-2xl font-bold font-mono mt-1" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
                                            {kpi.value}
                                        </p>
                                    </div>
                                    <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center flex-shrink-0`}>
                                        <Icon className={`w-5 h-5 ${kpi.color}`} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Platform Usage: Vuelos este mes + totales */}
            <div className="grid md:grid-cols-3 gap-4">
                <Card className="card-aviation">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold font-mono" data-testid="flights-month">{usage.flights_this_month || 0}</p>
                        <p className="text-xs text-muted-foreground uppercase mt-1">Vuelos Este Mes</p>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold font-mono" data-testid="flights-total">{usage.total_flights || 0}</p>
                        <p className="text-xs text-muted-foreground uppercase mt-1">Vuelos Totales</p>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold font-mono" data-testid="tickets-open">{support.open_tickets || 0}</p>
                        <p className="text-xs text-muted-foreground uppercase mt-1">Tickets Soporte Abiertos</p>
                    </CardContent>
                </Card>
            </div>

            {/* Per-Company Usage Table */}
            <Card className="card-aviation">
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" /> Uso por Empresa (lo que facturas)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {tenantUsage.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">Sin empresas registradas</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table-aviation w-full" data-testid="tenant-usage-table">
                                <thead>
                                    <tr>
                                        <th className="text-left">Empresa</th>
                                        <th className="text-center">Tipo</th>
                                        <th className="text-center">Plan</th>
                                        <th className="text-center">Estado</th>
                                        <th className="text-center">Usuarios</th>
                                        <th className="text-center">Aeronaves</th>
                                        <th className="text-center">Vuelos/Mes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenantUsage.map(t => (
                                        <tr key={t.id}>
                                            <td className="font-medium">{t.name || '-'}</td>
                                            <td className="text-center">
                                                <Badge className={t.tenant_type === 'ground_handler' ? 'bg-violet-500/10 text-violet-400' : 'bg-cyan-500/10 text-cyan-400'} variant="outline">
                                                    {t.tenant_type === 'ground_handler' ? 'GH' : 'Airline'}
                                                </Badge>
                                            </td>
                                            <td className="text-center"><Badge className={planColors[t.plan] || ''}>{t.plan}</Badge></td>
                                            <td className="text-center"><Badge className={statusColors[t.status] || ''}>{statusLabels[t.status] || t.status}</Badge></td>
                                            <td className="text-center font-mono font-bold">{t.users}</td>
                                            <td className="text-center font-mono font-bold">{t.aircraft}</td>
                                            <td className="text-center font-mono font-bold">{t.flights_month}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Plan Distribution + Activity */}
            <div className="grid md:grid-cols-2 gap-6">
                <Card className="card-aviation">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground">
                            Distribucion por Plan
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {Object.entries(tenants.by_plan || {}).map(([plan, count]) => (
                                <div key={plan} className="flex items-center justify-between">
                                    <Badge className={planColors[plan] || 'bg-muted text-muted-foreground'}>
                                        {plan.charAt(0).toUpperCase() + plan.slice(1)}
                                    </Badge>
                                    <span className="font-mono font-bold">{count}</span>
                                </div>
                            ))}
                            {Object.keys(tenants.by_plan || {}).length === 0 && (
                                <p className="text-sm text-muted-foreground text-center py-4">Sin datos</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-aviation">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground">
                            Actividad Reciente
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {(data?.recent_activities || []).slice(0, 8).map((act, i) => (
                                <div key={i} className="flex items-center gap-3 py-1.5 border-b border-border/30 last:border-0">
                                    <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs truncate">
                                            <span className="font-medium">{act.user_email}</span>
                                            <span className="text-muted-foreground"> — {act.action}</span>
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {(data?.recent_activities || []).length === 0 && (
                                <p className="text-sm text-muted-foreground text-center py-4">Sin actividad</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
            {admin?.role === 'superadmin' && <BackupsCard />}
            {admin?.role === 'superadmin' && <EmailCenterCard />}
        </div>
    );
}
